import type { PlanCatalogPriceDTO } from '@kanbu/schema/contracts';
import { BillingInterval } from '@kanbu/schema/enums';

export type BillingPeriodKey = 'month' | 'quarter' | 'half_year' | 'year';

export type BillingPeriodOption = {
  key: BillingPeriodKey;
  /** Stripe recurring interval for this period. */
  interval: BillingInterval;
  /** Stripe recurring interval_count (1 monthly/yearly, 3 quarterly, 6 half-yearly). */
  intervalCount: number;
  /** Length in months, used for mixed-interval alignment. */
  months: number;
};

export const BILLING_PERIOD_OPTIONS: BillingPeriodOption[] = [
  {
    key: 'month',
    interval: BillingInterval.Month,
    intervalCount: 1,
    months: 1,
  },
  {
    key: 'quarter',
    interval: BillingInterval.Month,
    intervalCount: 3,
    months: 3,
  },
  {
    key: 'half_year',
    interval: BillingInterval.Month,
    intervalCount: 6,
    months: 6,
  },
  { key: 'year', interval: BillingInterval.Year, intervalCount: 1, months: 12 },
];

export function getBillingPeriodOption(
  key: BillingPeriodKey,
): BillingPeriodOption {
  return (
    BILLING_PERIOD_OPTIONS.find(option => option.key === key) ??
    BILLING_PERIOD_OPTIONS[0]
  );
}

/** Matches a catalog price to a billing period by interval + interval_count. */
export function catalogPriceMatchesPeriod(
  price: Pick<PlanCatalogPriceDTO, 'interval' | 'intervalCount'>,
  period: BillingPeriodOption,
): boolean {
  return (
    price.interval === period.interval &&
    price.intervalCount === period.intervalCount
  );
}

export function validateMixedIntervalAlignment(
  periodKeys: BillingPeriodKey[],
): {
  valid: boolean;
  shortestMonths?: number;
} {
  if (periodKeys.length <= 1) {
    return { valid: true };
  }

  const monthCounts = periodKeys.map(key => getBillingPeriodOption(key).months);
  const shortestMonths = Math.min(...monthCounts);

  const valid = monthCounts.every(months => months % shortestMonths === 0);

  return { valid, shortestMonths };
}
